import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../../utils/baseQuery";

/**
 * Parámetros OAuth con los que Claude pide conectarse al MCP de Intelligence. Llegan en la
 * URL de la pantalla de autorización y se reenvían tal cual al backend, que los valida.
 */
export interface McpAuthorizationRequest {
  client_id: string;
  redirect_uri: string;
  response_type: string;
  code_challenge: string;
  code_challenge_method: string;
  state?: string;
  scope?: string;
  resource?: string;
}

interface ApiResponse<T> {
  status: number;
  data: T;
  message?: string;
}

export interface McpAuthorizationInfo {
  clientName: string;
  redirectHost: string;
}

const REQUIRED_KEYS = [
  "client_id",
  "redirect_uri",
  "response_type",
  "code_challenge",
  "code_challenge_method",
] as const;
const OPTIONAL_KEYS = ["state", "scope", "resource"] as const;

/**
 * Lee los parámetros de la URL. Solo los conocidos: el backend rechaza campos de más.
 * Null si falta alguno obligatorio.
 */
export function authorizationRequestFrom(
  search: string
): McpAuthorizationRequest | null {
  const query = new URLSearchParams(search);
  const request: Record<string, string> = {};
  for (const key of REQUIRED_KEYS) {
    const value = query.get(key);
    if (!value) return null;
    request[key] = value;
  }
  for (const key of OPTIONAL_KEYS) {
    const value = query.get(key);
    if (value) request[key] = value;
  }
  return request as unknown as McpAuthorizationRequest;
}

export const mcpApi = createApi({
  reducerPath: "mcpApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    validateMcpAuthorization: builder.mutation<
      ApiResponse<McpAuthorizationInfo>,
      McpAuthorizationRequest
    >({
      query: (body) => ({ url: "/mcp/oauth/validate", method: "POST", body }),
    }),
    approveMcpAuthorization: builder.mutation<
      ApiResponse<{ redirectUrl: string }>,
      McpAuthorizationRequest
    >({
      query: (body) => ({ url: "/mcp/oauth/approve", method: "POST", body }),
    }),
    denyMcpAuthorization: builder.mutation<
      ApiResponse<{ redirectUrl: string }>,
      McpAuthorizationRequest
    >({
      query: (body) => ({ url: "/mcp/oauth/deny", method: "POST", body }),
    }),
  }),
});

export const {
  useValidateMcpAuthorizationMutation,
  useApproveMcpAuthorizationMutation,
  useDenyMcpAuthorizationMutation,
} = mcpApi;
